import { useEffect, useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';

export interface TimeEntry {
  id: string;
  task_id: string;
  user_id: string;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  notes: string | null;
}

const ACTIVE_KEY = ['task-timer-active'];
const ENTRIES_KEY = (taskId?: string) => ['task-time-entries', taskId ?? 'all'];

export const formatDuration = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

/** Hook for an individual task's timer + entries history */
export const useTaskTimer = (taskId?: string) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [tick, setTick] = useState(0);

  // Currently running entry across ALL tasks for this user
  const { data: activeEntry } = useQuery<TimeEntry | null>({
    queryKey: [...ACTIVE_KEY, user?.id],
    enabled: !!user,
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_time_entries')
        .select('*')
        .is('ended_at', null)
        .order('started_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as TimeEntry | null) ?? null;
    },
  });

  // Entries for this specific task
  const { data: entries = [] } = useQuery<TimeEntry[]>({
    queryKey: ENTRIES_KEY(taskId),
    enabled: !!user && !!taskId,
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_time_entries')
        .select('*')
        .eq('task_id', taskId!)
        .order('started_at', { ascending: false })
        .limit(50);
      if (error) throw error;
      return (data as TimeEntry[]) ?? [];
    },
  });

  // Force re-render every second when a timer is running
  useEffect(() => {
    if (!activeEntry) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [activeEntry]);

  const isRunningForThisTask = !!activeEntry && activeEntry.task_id === taskId;
  const elapsedSeconds = activeEntry
    ? Math.floor((Date.now() - new Date(activeEntry.started_at).getTime()) / 1000)
    : 0;

  const totalSeconds = entries.reduce((acc, e) => acc + (e.duration_seconds ?? 0), 0);

  const startMutation = useMutation({
    mutationFn: async () => {
      if (!user || !taskId) throw new Error('Missing user or task');
      // Stop any other active timer first
      if (activeEntry && activeEntry.task_id !== taskId) {
        await supabase
          .from('task_time_entries')
          .update({ ended_at: new Date().toISOString() })
          .eq('id', activeEntry.id);
      }
      const { data, error } = await supabase
        .from('task_time_entries')
        .insert({ task_id: taskId, user_id: user.id, started_at: new Date().toISOString() })
        .select()
        .single();
      if (error) throw error;
      return data as TimeEntry;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACTIVE_KEY });
      queryClient.invalidateQueries({ queryKey: ENTRIES_KEY(taskId) });
      toast({ title: 'Cronómetro iniciado', description: 'Registrando tiempo en la tarea.' });
    },
    onError: () => toast({ title: 'Error', description: 'No se pudo iniciar el cronómetro.', variant: 'destructive' }),
  });

  const stopMutation = useMutation({
    mutationFn: async () => {
      if (!activeEntry) throw new Error('No active timer');
      const { error } = await supabase
        .from('task_time_entries')
        .update({ ended_at: new Date().toISOString() })
        .eq('id', activeEntry.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ACTIVE_KEY });
      queryClient.invalidateQueries({ queryKey: ENTRIES_KEY(taskId) });
      queryClient.invalidateQueries({ queryKey: ['task-time-entries'] });
      toast({ title: 'Cronómetro detenido', description: 'Tiempo guardado correctamente.' });
    },
    onError: () => toast({ title: 'Error', description: 'No se pudo detener el cronómetro.', variant: 'destructive' }),
  });

  const start = useCallback(() => startMutation.mutate(), [startMutation]);
  const stop = useCallback(() => stopMutation.mutate(), [stopMutation]);

  return {
    activeEntry,
    isRunningForThisTask,
    isAnotherTaskRunning: !!activeEntry && activeEntry.task_id !== taskId,
    elapsedSeconds,
    entries,
    totalSeconds,
    start,
    stop,
    isPending: startMutation.isPending || stopMutation.isPending,
    _tick: tick,
  };
};
